import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    Skeleton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';

import { CheckCircle, HourglassEmpty, Cancel } from '@mui/icons-material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';

import { fetcher, notificationApi } from '../../lib/api';
import styles from './Notifications.module.scss';

const PAGE_SIZE = 30;

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const Notifications = () => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [kind, setKind] = useState('all');
    const [rows, setRows] = useState([]);
    const [expandedId, setExpandedId] = useState(null);

    const listUrl = notificationApi.list({
        page,
        limit: PAGE_SIZE,
        ...(kind !== 'all' ? { kind } : {}),
    });

    const { data, error, isLoading } = useSWR(listUrl, fetcher, {
        refreshInterval: 15000,
    });

    useEffect(() => {
        setPage(1);
        setRows([]);
    }, [kind]);

    useEffect(() => {
        if (!data?.results) return;
        setRows((prev) => {
            if (page === 1) return data.results;
            const byKey = new Map(prev.map((r) => [`${r.kind}:${r.id}`, r]));
            data.results.forEach((r) => byKey.set(`${r.kind}:${r.id}`, r));
            return Array.from(byKey.values());
        });
    }, [data, page]);

    const filters = useMemo(
        () => [
            { id: 'all', label: t('notifications.filter_all') },
            { id: 'batch', label: t('notifications.filter_batch') },
            { id: 'single', label: t('notifications.filter_single') },
        ],
        [t]
    );

    const totalCount = data?.count ?? rows.length;
    const hasMore = rows.length > 0 && rows.length < totalCount;

    const sourceLabel = (source) => {
        const map = {
            offer: t('notifications.source_offer'),
            group_access: t('notifications.source_group_access'),
            admin: t('notifications.source_admin'),
            generic: t('notifications.source_generic'),
        };
        return map[source] || source || '—';
    };

    const statusLabel = (status) => {
        if (status === 'sent') return t('notifications.status_sent');
        if (status === 'pending') return t('notifications.status_pending');
        if (status === 'failed') return t('notifications.status_failed');
        return status || '—';
    };

    const statusClass = (status) => {
        if (status === 'sent') return styles.statusSent;
        if (status === 'pending') return styles.statusPending;
        if (status === 'failed') return styles.statusFailed;
        return '';
    };

    return (
        <GridContainer>
            <GridItem xs={12}>
                <SparaTitle title={t('menu.push_notifications')} />
            </GridItem>
            <GridItem xs={12}>
                <Card>
                    <CardBody>
                        <div className={styles.listHeader}>
                            <div className={styles.listHeaderCount}>
                                {totalCount} {t('menu.push_notifications').toLowerCase()}
                            </div>
                            <div className={styles.filters}>
                                {filters.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        className={`${styles.filterChip} ${
                                            kind === f.id ? styles.filterChipActive : ''
                                        }`}
                                        onClick={() => setKind(f.id)}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={styles.tableWrap}>
                            <Table>
                                <TableHead className={styles.tableHead}>
                                    <TableRow>
                                        <TableCell>{t('notifications.col_kind')}</TableCell>
                                        <TableCell>{t('notifications.col_summary')}</TableCell>
                                        <TableCell>{t('notifications.col_status')}</TableCell>
                                        <TableCell>{t('notifications.col_created')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody className={styles.tableBody}>
                                    {isLoading && page === 1 && !rows.length && (
                                        <>
                                            {[1, 2, 3, 4, 5].map((i) => (
                                                <TableRow key={`sk-${i}`}>
                                                    <TableCell colSpan={5}>
                                                        <Skeleton height={28} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </>
                                    )}

                                    {error && (
                                        <TableRow>
                                            <TableCell colSpan={5} className={styles.empty}>
                                                {t('error')}
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {!isLoading && !error && rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} className={styles.empty}>
                                                {t('notifications.empty')}
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {rows.map((row) => {
                                        const rowKey = `${row.kind}:${row.id}`;
                                        const isBatch = row.kind === 'batch';
                                        const expanded = expandedId === rowKey;

                                        return (
                                            <React.Fragment key={rowKey}>
                                                <TableRow>
                                                    <TableCell>
                                                        <span
                                                            className={`${styles.badge} ${
                                                                isBatch
                                                                    ? styles.kindBatch
                                                                    : styles.kindSingle
                                                            }`}
                                                        >
                                                            {isBatch
                                                                ? t('notifications.kind_batch')
                                                                : t('notifications.kind_single')}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className={styles.notificationPreview}>
                                                            {row.image ? (
                                                                <img
                                                                    src={row.image}
                                                                    alt=""
                                                                    className={styles.notificationImage}
                                                                />
                                                            ) : (
                                                                <div
                                                                    className={
                                                                        styles.notificationImagePlaceholder
                                                                    }
                                                                />
                                                            )}
                                                            <div className={styles.summary}>
                                                                <div className={styles.summaryTitle}>
                                                                    {row.title ||
                                                                        (isBatch
                                                                            ? row.offer?.title ||
                                                                              t(
                                                                                  'notifications.unknown_offer'
                                                                              )
                                                                            : sourceLabel(
                                                                                  row.source
                                                                              ))}
                                                                </div>
                                                                {row.body && (
                                                                    <div
                                                                        className={
                                                                            styles.summaryBody
                                                                        }
                                                                    >
                                                                        {row.body}
                                                                    </div>
                                                                )}
                                                                <div
                                                                    className={
                                                                        styles.summaryMeta
                                                                    }
                                                                >
                                                                    {isBatch ? (
                                                                        <div
                                                                            className={
                                                                                styles.deliveryPills
                                                                            }
                                                                        >
                                                                            <span
                                                                                className={`${styles.deliveryPill} ${styles.deliveryPillSent}`}
                                                                            >
                                                                                <CheckCircle
                                                                                    className={
                                                                                        styles.deliveryPillIcon
                                                                                    }
                                                                                />
                                                                                {row.sent ?? 0}{' '}
                                                                                {t(
                                                                                    'notifications.delivery_sent'
                                                                                )}
                                                                            </span>
                                                                            <span
                                                                                className={`${styles.deliveryPill} ${styles.deliveryPillPending}`}
                                                                            >
                                                                                <HourglassEmpty
                                                                                    className={
                                                                                        styles.deliveryPillIcon
                                                                                    }
                                                                                />
                                                                                {row.pending ?? 0}{' '}
                                                                                {t(
                                                                                    'notifications.delivery_pending'
                                                                                )}
                                                                            </span>
                                                                            <span
                                                                                className={`${styles.deliveryPill} ${styles.deliveryPillFailed}`}
                                                                            >
                                                                                <Cancel
                                                                                    className={
                                                                                        styles.deliveryPillIcon
                                                                                    }
                                                                                />
                                                                                {row.failed ?? 0}{' '}
                                                                                {t(
                                                                                    'notifications.delivery_failed'
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        sourceLabel(row.source)
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {isBatch ? (
                                                            <span className={styles.delivery}>
                                                                {row.total ?? 0}{' '}
                                                                {t('notifications.total')}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className={`${styles.badge} ${statusClass(
                                                                    row.status
                                                                )}`}
                                                            >
                                                                {statusLabel(row.status)}
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDate(row.created_at)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {!isBatch && row.error_message && (
                                                            <button
                                                                type="button"
                                                                className={styles.filterChip}
                                                                onClick={() =>
                                                                    setExpandedId(
                                                                        expanded ? null : rowKey
                                                                    )
                                                                }
                                                            >
                                                                {expanded
                                                                    ? t(
                                                                          'notifications.hide_error'
                                                                      )
                                                                    : t(
                                                                          'notifications.show_error'
                                                                      )}
                                                            </button>
                                                        )}
                                                        {isBatch && (
                                                            <span
                                                                className={styles.mono}
                                                                title={row.id}
                                                            >
                                                                {row.id}
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                {expanded && row.error_message && (
                                                    <TableRow>
                                                        <TableCell colSpan={5}>
                                                            <div className={styles.exceptionBox}>
                                                                {row.error_message}
                                                            </div>
                                                            {row.link && (
                                                                <div
                                                                    style={{ marginTop: 8 }}
                                                                    className={styles.mono}
                                                                >
                                                                    {row.link}
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        {hasMore && (
                            <div className={styles.loadMore}>
                                <SparaButton
                                    onClick={() => setPage((p) => p + 1)}
                                    disabled={isLoading}
                                >
                                    {t('notifications.load_more')}
                                </SparaButton>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default Notifications;
